import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";

import type { PayoutBatchDetail } from "../../types/payoutBatches.types";

export interface SubmitForApprovalDialogProps {
  batch: PayoutBatchDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  isPending: boolean;
  errorMessage: string | null;
}

/** Nothing is paid here: the selection is frozen and a second user decides. So no step-up code. */
export function SubmitForApprovalDialog({
  batch,
  open,
  onOpenChange,
  onConfirm,
  isPending,
  errorMessage,
}: SubmitForApprovalDialogProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  const source = batch.source_currency;

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isPending) return;
        onOpenChange(next);
      }}
      size="sm"
      title={t("submit_title")}
      description={t("submit_description")}
      showCloseButton={!isPending}
      footer={
        <>
          <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={isPending} />}>
            {t("action_close")}
          </ColocaDialogClose>
          <ColocaButton variant="primary" loading={isPending} onClick={() => void onConfirm()}>
            {t("submit_confirm")}
          </ColocaButton>
        </>
      }
    >
      <div className="space-y-3">
        <dl className="grid grid-cols-2 gap-3 rounded-lg border border-border p-3 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">{t("submit_rows_label")}</dt>
            <dd className="font-mono tabular-nums text-foreground">{batch.counters.selected}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t("submit_debit_label")}</dt>
            <dd className="font-mono tabular-nums text-foreground">
              {`${formatCurrencyAmount(batch.totals.debit_indicative_selected, source, activeLanguage)} ${source}`}
            </dd>
          </div>
        </dl>
        <p className="text-sm text-muted-foreground">{t("submit_frozen_note")}</p>
        {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
      </div>
    </ColocaDialog>
  );
}
